import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { getTournament, addTeam, addSinglesEntry, addSoloEntrant, removeSoloEntrant, pairSoloPlayers, removeTeam, withdrawTeam, unwithdrawTeam, applyGroupWalkoversForWithdrawnTeam, revertWalkoversAgainstTeamInGroupStage, autoAssignTeamsToGroups, generateGroupMatches, updateGroupAssignments, recordMatchScore, updateTournament, updateGroupMatchesBestOf, updateMatchBestOf, createKnockoutBracketFromSelectedTeams, updateKnockoutMatchTeams, advanceWinnersToNextRound, getBestOfForRound, clearTournamentGroups } from '../services/tournamentService';
import { entryToLegacyTeamShape, isSinglesTournament, formatTournamentEntryLabel } from '../utils/tournamentFormat';
import { normalizeScoringConfig, getWalkoverPointsFor } from '../utils/gameScoreRules';
import { syncTournamentGroups, assignCourtToGroup } from '../services/tournamentGroupService';
import { getAllGroupStandings } from '../services/tournamentStandingsService';
import { supabase } from '../utils/supabase';
import { Button, BackButton, StatusBadge, InfoHintButton } from '../components/common';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import Tabs from '../components/common/Tabs';
import ConfirmModal from '../components/common/ConfirmModal';
import AddTeamSection from '../components/tournaments/AddTeamSection';
import TournamentPairingSection from '../components/tournaments/TournamentPairingSection';
import EditGroupsSection from '../components/tournaments/EditGroupsSection';
import TournamentMatchScoreModal from '../components/tournaments/TournamentMatchScoreModal';
import EditTournamentSettingsPanel from '../components/tournaments/EditTournamentSettingsPanel';
import GroupStandingsTables from '../components/tournaments/GroupStandingsTables';
import TournamentMatchCard from '../components/tournaments/TournamentMatchCard';
import GroupMatchSection from '../components/tournaments/GroupMatchSection';
import GenerateKnockoutModal from '../components/tournaments/GenerateKnockoutModal';
import EditKnockoutMatchupModal from '../components/tournaments/EditKnockoutMatchupModal';
import EditKnockoutMatchupsModal from '../components/tournaments/EditKnockoutMatchupsModal';
import KnockoutBracketTree from '../components/tournaments/KnockoutBracketTree';
import TournamentProgressStepper from '../components/tournaments/TournamentProgressStepper';
import TournamentTimeline from '../components/tournaments/TournamentTimeline';
import ActionDashboard from '../components/tournaments/ActionDashboard';
import MyTournamentProgress from '../components/tournaments/MyTournamentProgress';
import TournamentHighlights from '../components/tournaments/TournamentHighlights';
import { getGenderTextColor } from '../utils/genderColors';
import { formatPlayerName } from '../utils/nameUtils';
import { countPendingGroupMatchesForTeam } from '../utils/tournamentStandings';
import { getTeamSeedsForMatch } from '../utils/tournamentBracketUtils';
import { 
  getCurrentTournamentStep, 
  getTournamentSteps, 
  getTournamentActionItems, 
  getTournamentStatistics,
  getUserTournamentProgress 
} from '../utils/tournamentProgress';
import {
  getTournamentStatusLabel,
  getTournamentStatusVariant,
  getTournamentStatusDescription
} from '../utils/tournamentStatusDisplay';
import { format } from 'date-fns';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';

const TOURNAMENT_DETAIL_PAGE_SHELL = `${APP_PAGE_BLEED_ROOT} min-h-screen bg-background`;
const TOURNAMENT_DETAIL_PAGE_INNER = `mx-auto max-w-app-shell ${APP_PAGE_GUTTER} pt-3 pb-8 md:pt-6`;

const BRACKET_ROUND_LABELS = {
  2: 'Final only',
  4: 'Semi Finals',
  8: 'Quarter Finals (QF)',
  16: 'Round of 16',
  32: 'Round of 32'
};

const GROUP_COURT_ASSIGN_HELP =
  "Pin this group's matches to a numbered court, or leave Auto (any court) to spread play across courts.";

/** Hover/focus tooltip (portal) — native title + cursor-help is unreliable and shows a misleading “?” cursor. */
function GroupCourtHelpIcon() {
  const [show, setShow] = useState(false);
  const anchorRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState({});

  useEffect(() => {
    if (!show || !anchorRef.current) return;

    const position = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const tooltipWidth = 260;
      let top = rect.bottom + 8;
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      if (left < 8) left = 8;
      if (left + tooltipWidth > window.innerWidth - 8) {
        left = window.innerWidth - tooltipWidth - 8;
      }
      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${tooltipWidth}px`,
        zIndex: 99999
      });
    };

    position();
    window.addEventListener('scroll', position, true);
    window.addEventListener('resize', position);
    return () => {
      window.removeEventListener('scroll', position, true);
      window.removeEventListener('resize', position);
    };
  }, [show]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="inline-flex shrink-0 rounded p-0.5 text-text-muted hover:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-default"
        aria-label={GROUP_COURT_ASSIGN_HELP}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
      {show &&
        createPortal(
          <div
            className="pointer-events-none rounded-md border border-border-medium bg-surface px-2.5 py-2 text-left text-xs leading-snug text-text-primary shadow-lg"
            style={tooltipStyle}
            role="tooltip"
          >
            {GROUP_COURT_ASSIGN_HELP}
          </div>,
          document.body
        )}
    </>
  );
}

/** Match is effectively complete only when games satisfy the format (e.g. Best of 3 needs 2 wins) */
const isMatchEffectivelyComplete = (match) => {
  const bestOf = match.best_of ?? 1;
  const gamesNeeded = Math.ceil(bestOf / 2);
  const games = match.games || [];
  const team1Wins = games.filter((g) => g.winning_team === 1).length;
  const team2Wins = games.filter((g) => g.winning_team === 2).length;
  return (team1Wins >= gamesNeeded || team2Wins >= gamesNeeded) && games.length > 0;
};

/** List/bracket filter: treat walkover/forfeit/completed or BoN-complete as “done” */
const isTournamentMatchResolved = (match) =>
  ['walkover', 'forfeit', 'completed'].includes(match.status) || isMatchEffectivelyComplete(match);

/** Group stage has started (scores or non-scheduled status) — don’t allow auto/random reassignment */
const groupStageHasRecordedPlay = (matchList) =>
  (matchList || []).some(
    (m) =>
      m.round === 'group' &&
      (m.status !== 'scheduled' || (Array.isArray(m.games) && m.games.length > 0))
  );

const TournamentDetailPage = ({ currentUser }) => {
  const { clubId, tournamentId } = useParams();
  const navigate = useNavigate();
  
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [groupStandings, setGroupStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [showGenerateKnockoutModal, setShowGenerateKnockoutModal] = useState(false);
  const [generatingKnockout, setGeneratingKnockout] = useState(false);
  const [matchForEditMatchup, setMatchForEditMatchup] = useState(null);
  const [showEditMatchupsModal, setShowEditMatchupsModal] = useState(false);
  const [savingMatchup, setSavingMatchup] = useState(false);
  const [knockoutView, setKnockoutView] = useState('list'); // 'list' | 'bracket'
  const [advancingWinners, setAdvancingWinners] = useState(false);
  const [teamToRemoveId, setTeamToRemoveId] = useState(null);
  const [teamToWithdrawId, setTeamToWithdrawId] = useState(null);
  const [withdrawingTeam, setWithdrawingTeam] = useState(false);
  const [walkoverApplyPrompt, setWalkoverApplyPrompt] = useState(null);
  const [applyingWalkovers, setApplyingWalkovers] = useState(false);
  const [teamToUnwithdrawId, setTeamToUnwithdrawId] = useState(null);
  const [unwithdrawingTeam, setUnwithdrawingTeam] = useState(false);
  const [configureGroupCount, setConfigureGroupCount] = useState(2);
  const seedingDefaultGroupsInFlightRef = useRef(false);
  const [showClearGroupsConfirm, setShowClearGroupsConfirm] = useState(false);
  const [clearingGroups, setClearingGroups] = useState(false);
  const [showReduceGroupCountConfirm, setShowReduceGroupCountConfirm] = useState(false);
  const [pendingReduceGroupCount, setPendingReduceGroupCount] = useState(null);
  const [applyingGroupCount, setApplyingGroupCount] = useState(false);
  const [groupStageView, setGroupStageView] = useState('group'); // 'group' | 'court'
  const [hideCompletedGroupMatches, setHideCompletedGroupMatches] = useState(false);
  const [hideCompletedKnockoutMatches, setHideCompletedKnockoutMatches] = useState(false);
  /** Sub-section within the combined Group stage tab */
  const [groupHubSection, setGroupHubSection] = useState('groups');
  const [canManageTeams, setCanManageTeams] = useState(false);

  useEffect(() => {
    if (tournamentId) {
      fetchTournamentData();
      fetchClubPlayers();
    }
  }, [tournamentId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!clubId || !currentUser?.id) {
        setCanManageTeams(false);
        return;
      }
      if (currentUser.role === 'admin' || currentUser.role === 'super_admin') {
        setCanManageTeams(true);
        return;
      }
      const { data } = await supabase
        .from('players_club')
        .select('club_role')
        .eq('club_id', clubId)
        .eq('player_id', currentUser.id)
        .maybeSingle();
      if (!cancelled) {
        const r = data?.club_role;
        setCanManageTeams(['owner', 'admin', 'host'].includes(r || ''));
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [clubId, currentUser]);

  // Sync configure group count from tournament when it loads
  useEffect(() => {
    if (tournament?.group_count != null) {
      setConfigureGroupCount(tournament.group_count);
    }
  }, [tournament?.group_count]);

  const fetchTournamentData = async (options = {}) => {
    const { silent = false } = options; // When true, skip loading state (for post-save refresh)
    try {
      if (!silent) setLoading(true);
      const data = await getTournament(tournamentId);
      // Use tournament_entries as source of truth (withdrawn_at, etc.); avoids stale nested team1/team2 after updates.
      const entriesById = Object.fromEntries((data.tournament_entries || []).map((e) => [e.id, e]));
      const entryToLegacy = (row) => (row ? entryToLegacyTeamShape(row) : null);
      if (data.tournament_matches?.length) {
        data.tournament_matches = data.tournament_matches.map((m) => ({
          ...m,
          team1: m.entry1_id ? entryToLegacy(entriesById[m.entry1_id]) ?? m.team1 : entryToLegacy(m.team1),
          team2: m.entry2_id ? entryToLegacy(entriesById[m.entry2_id]) ?? m.team2 : entryToLegacy(m.team2),
          winner_team: m.winner_entry_id
            ? entryToLegacy(entriesById[m.winner_entry_id]) ?? m.winner_team
            : entryToLegacy(m.winner_team)
        }));
      }
      // Fetch session_games for all matches (completed + partial saves)
      const allMatchIds = (data.tournament_matches || []).map(m => m.id);
      if (allMatchIds.length > 0) {
        const { data: games } = await supabase
          .from('session_games')
          .select('tournament_match_id, team_1_score, team_2_score, game_order, winning_team')
          .in('tournament_match_id', allMatchIds)
          .order('game_order');
        const gamesByMatch = {};
        (games || []).forEach(g => {
          if (!gamesByMatch[g.tournament_match_id]) gamesByMatch[g.tournament_match_id] = [];
          gamesByMatch[g.tournament_match_id].push(g);
        });
        data.tournament_matches = data.tournament_matches.map(m => ({
          ...m,
          games: gamesByMatch[m.id] || []
        }));
      }
      setTournament(data);

      // Standings are derived from current groups + group matches. Refresh whenever we're in an active stage;
      // clear client state when not (e.g. after clearing groups we return to draft — old standings must not linger).
      if (['group_stage', 'knockout', 'completed'].includes(data.status)) {
        const standings = await getAllGroupStandings(tournamentId);
        setGroupStandings(standings);
      } else {
        setGroupStandings([]);
      }
      return data;
    } catch (err) {
      console.error('Error fetching tournament:', err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchClubPlayers = async () => {
    try {
      const { data, error } = await supabase
        .from('players_club')
        .select(`
          player_id,
          players (*)
        `)
        .eq('club_id', clubId)
        .eq('is_active', true);

      if (error) throw error;
      setPlayers(data?.map(pc => pc.players) || []);
    } catch (err) {
      console.error('Error fetching players:', err);
    }
  };

  const handleAddTeam = async (player1Id, player2Id) => {
    try {
      const newEntry = await addTeam(tournamentId, player1Id, player2Id);
      setTournament((prev) => ({
        ...prev,
        tournament_entries: [...(prev.tournament_entries || []), newEntry]
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddSinglesEntry = async (playerId) => {
    try {
      const newEntry = await addSinglesEntry(tournamentId, playerId);
      setTournament((prev) => ({
        ...prev,
        tournament_entries: [...(prev.tournament_entries || []), newEntry]
      }));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const handleRegisterSolo = async (playerId) => {
    try {
      const row = await addSoloEntrant(tournamentId, playerId);
      setTournament((prev) => ({
        ...prev,
        tournament_solo_entrants: [...(prev.tournament_solo_entrants || []), row]
      }));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const handleRemoveSolo = async (playerId) => {
    try {
      await removeSoloEntrant(tournamentId, playerId);
      setTournament((prev) => ({
        ...prev,
        tournament_solo_entrants: (prev.tournament_solo_entrants || []).filter(
          (s) => s.player_id !== playerId
        )
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePairSolos = async (player1Id, player2Id) => {
    try {
      const entry = await pairSoloPlayers(tournamentId, player1Id, player2Id);
      setTournament((prev) => ({
        ...prev,
        tournament_entries: [...(prev.tournament_entries || []), entry],
        tournament_solo_entrants: (prev.tournament_solo_entrants || []).filter(
          (s) => s.player_id !== player1Id && s.player_id !== player2Id
        )
      }));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const handleRemoveTeamClick = (teamId) => {
    setTeamToRemoveId(teamId);
  };

  const handleConfirmRemoveTeam = async () => {
    if (!teamToRemoveId) return;
    try {
      await removeTeam(tournamentId, teamToRemoveId);
      setTournament((prev) => ({
        ...prev,
        tournament_entries: (prev.tournament_entries || []).filter((t) => t.id !== teamToRemoveId)
      }));
      fetchTournamentData({ silent: true }); // Sync groups in background
    } catch (err) {
      setError(err.message);
    } finally {
      setTeamToRemoveId(null);
    }
  };

  const handleConfirmWithdrawTeam = async () => {
    if (!teamToWithdrawId) return;
    const matchList = [...(tournament?.tournament_matches || [])].sort(
      (a, b) => (a.match_order ?? 0) - (b.match_order ?? 0)
    );
    const pendingWalkovers = countPendingGroupMatchesForTeam(matchList, teamToWithdrawId);
    const withdrawnId = teamToWithdrawId;
    setWithdrawingTeam(true);
    try {
      await withdrawTeam(withdrawnId);
      await fetchTournamentData({ silent: true });
      if (pendingWalkovers > 0) {
        setWalkoverApplyPrompt({ teamId: withdrawnId, count: pendingWalkovers });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setWithdrawingTeam(false);
      setTeamToWithdrawId(null);
    }
  };

  const handleConfirmApplyWalkoversAfterWithdraw = async () => {
    if (!walkoverApplyPrompt) return;
    setApplyingWalkovers(true);
    try {
      await applyGroupWalkoversForWithdrawnTeam(
        tournamentId,
        walkoverApplyPrompt.teamId,
        clubId
      );
      await fetchTournamentData({ silent: true });
      setWalkoverApplyPrompt(null);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setApplyingWalkovers(false);
    }
  };

  const handleConfirmUnwithdrawTeam = async () => {
    if (!teamToUnwithdrawId) return;
    const teamId = teamToUnwithdrawId;
    setUnwithdrawingTeam(true);
    try {
      await unwithdrawTeam(teamId);
      await revertWalkoversAgainstTeamInGroupStage(tournamentId, teamId, clubId);
      await fetchTournamentData({ silent: true });
      setTeamToUnwithdrawId(null);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUnwithdrawingTeam(false);
    }
  };

  const handleGuestAdded = async (guest) => {
    await fetchClubPlayers();
    if (!canManageTeams || !guest?.id) return;
    try {
      if (singlesTournament) {
        await handleAddSinglesEntry(guest.id);
      } else {
        await handleRegisterSolo(guest.id);
      }
    } catch {
      // handleAddSinglesEntry / handleRegisterSolo already call setError
    }
  };

  const handleGuestUpdated = async () => {
    await fetchClubPlayers();
  };

  const handleUpdateGroupAssignments = async (assignments, unassignedTeams) => {
    if (unassignedTeams.length > 0 && tournament.status !== 'draft') {
      const ok = window.confirm(
        `${unassignedTeams.length} team(s) are not in any group. They will not appear in group-stage matches until assigned. Continue?`
      );
      if (!ok) throw new Error('Update cancelled');
    }

    try {
      await updateGroupAssignments(tournamentId, assignments);
      await fetchTournamentData();
    } catch (err) {
      console.error('Error updating group assignments:', err);
      setError(err.message);
      throw err;
    }
  };

  const handleOpenScoreModal = (match) => {
    if (!canManageTeams) return;
    setSelectedMatch(match);
    setShowScoreModal(true);
  };

  const handleSaveMatchScore = async ({ matchId, games, winnerTeamId }) => {
    try {
      await recordMatchScore(matchId, games, winnerTeamId, clubId, null);
      // Optimistic update: merge new games into match so list updates instantly
      setTournament((prev) => {
        if (!prev?.tournament_matches) return prev;
        const gamesForMatch = games.map((g, i) => ({
          team_1_score: g.team1Score,
          team_2_score: g.team2Score,
          winning_team: g.winner,
          game_order: i + 1
        }));
        return {
          ...prev,
          tournament_matches: prev.tournament_matches.map((m) =>
            m.id === matchId
              ? {
                  ...m,
                  status: winnerTeamId ? 'completed' : m.status,
                  winner_entry_id: winnerTeamId ?? m.winner_entry_id,
                  games: gamesForMatch
                }
              : m
          )
        };
      });
      setShowScoreModal(false);
      setSelectedMatch(null);
      // Refresh in background to sync standings, progression, etc.
      fetchTournamentData({ silent: true });
    } catch (err) {
      console.error('Error saving match score:', err);
      setError(err.message);
      throw err;
    }
  };

  const handleSaveTournamentSettings = async (updates) => {
    try {
      const groupCountChanged = updates.group_count && updates.group_count !== tournament.group_count;

      await updateTournament(tournamentId, updates);

      if (groupCountChanged) {
        await syncTournamentGroups(tournamentId, updates.group_count);
      }

      await fetchTournamentData();
    } catch (err) {
      console.error('Error updating tournament:', err);
      setError(err.message);
      throw err;
    }
  };

  const handleClearGroups = async () => {
    try {
      setClearingGroups(true);
      setError(null);
      await clearTournamentGroups(tournamentId);
      await fetchTournamentData();
      setShowClearGroupsConfirm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setClearingGroups(false);
    }
  };

  const applyGroupCountSync = async (count) => {
    const teamCount = teams.length;
    const maxG = Math.max(2, Math.floor(teamCount / 2));
    const clamped = Math.min(Math.max(2, count), maxG);
    if (Math.floor(teamCount / clamped) < 2) {
      throw new Error(
        `Need at least ${clamped * 2} teams for ${clamped} groups (at least 2 per group). You have ${teamCount}.`
      );
    }
    const teamsPerGroupStored = Math.floor(teamCount / clamped);
    await updateTournament(tournamentId, {
      group_count: clamped,
      teams_per_group: teamsPerGroupStored,
    });
    await syncTournamentGroups(tournamentId, clamped);
    setConfigureGroupCount(clamped);
    await fetchTournamentData();
  };

  const handleGroupsCountSelectChange = async (e) => {
    const next = parseInt(e.target.value, 10);
    if (Number.isNaN(next)) return;
    const current = groups.length;
    if (next === current) return;
    const teamCount = teams.length;
    const maxG = Math.max(2, Math.floor(teamCount / 2));
    const clamped = Math.min(Math.max(2, next), maxG);
    if (Math.floor(teamCount / clamped) < 2) {
      setError(
        `Need at least ${clamped * 2} teams for ${clamped} groups (at least 2 per group). You have ${teamCount}.`
      );
      return;
    }
    if (clamped < current) {
      setPendingReduceGroupCount(clamped);
      setShowReduceGroupCountConfirm(true);
      return;
    }
    try {
      setApplyingGroupCount(true);
      setError(null);
      await applyGroupCountSync(clamped);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplyingGroupCount(false);
    }
  };

  const handleConfirmReduceGroupCount = async () => {
    if (pendingReduceGroupCount == null) return;
    try {
      setApplyingGroupCount(true);
      setError(null);
      await applyGroupCountSync(pendingReduceGroupCount);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setApplyingGroupCount(false);
    }
  };

  const handleAutoAssignTeams = async (mode = 'balanced') => {
    const teamCount = tournament?.tournament_entries?.length ?? 0;
    if (teamCount < 4) {
      setError('At least 4 teams are required before assigning groups');
      return;
    }
    if (groupStageHasRecordedPlay(tournament?.tournament_matches)) {
      setError('Cannot auto-assign or randomise groups once group scores or match progress exist.');
      return;
    }
    try {
      setError(null);
      await autoAssignTeamsToGroups(tournamentId, { mode });
      await fetchTournamentData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateGroupFormat = async (bestOf) => {
    const value = parseInt(bestOf, 10);
    if (![1, 3, 5].includes(value)) return;
    try {
      setError(null);
      const newRoundFormats = {
        ...(tournament.round_formats || {}),
        group: value
      };
      await updateTournament(tournamentId, { round_formats: newRoundFormats });
      await updateGroupMatchesBestOf(tournamentId, value, tournament);
      setTournament(prev => prev ? { ...prev, round_formats: newRoundFormats } : null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateNumberOfCourts = async (e) => {
    const n = parseInt(e.target.value, 10);
    if (!Number.isFinite(n) || n < 1 || n > 10) return;
    try {
      setError(null);
      await updateTournament(tournamentId, { number_of_courts: n });
      setTournament((prev) => (prev ? { ...prev, number_of_courts: n } : null));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRegenerateMatches = async () => {
    try {
      await generateGroupMatches(tournamentId);
      await fetchTournamentData();
      setShowRegenerateConfirm(false);
      setActiveTab('group-stage');
      setGroupHubSection('standings');
    } catch (err) {
      console.error('Error regenerating matches:', err);
      setError(err.message);
    }
  };

  const handleGenerateKnockout = async (selectedTeams, includeBronze = false) => {
    try {
      setGeneratingKnockout(true);
      setError(null);
      await createKnockoutBracketFromSelectedTeams(tournamentId, selectedTeams, includeBronze);
      await fetchTournamentData();
      setShowGenerateKnockoutModal(false);
      setActiveTab('knockout');
    } catch (err) {
      console.error('Error generating knockout bracket:', err);
      setError(err.message);
      throw err;
    } finally {
      setGeneratingKnockout(false);
    }
  };

  const handleSaveMatchup = async ({ team1Id, team2Id }) => {
    if (!matchForEditMatchup) return;
    try {
      setSavingMatchup(true);
      setError(null);
      await updateKnockoutMatchTeams(matchForEditMatchup.id, team1Id, team2Id);
      await fetchTournamentData({ silent: true });
      setMatchForEditMatchup(null);
    } catch (err) {
      console.error('Error updating matchup:', err);
      setError(err.message);
      throw err;
    } finally {
      setSavingMatchup(false);
    }
  };

  const roundNeedsAdvancing = useMemo(() => {
    const knockoutMatches = (tournament?.tournament_matches || []).filter(m => m.round !== 'group');
    const rounds = [
      { round: 'round_of_16', nextRound: 'quarter_final', label: 'Quarter Finals' },
      { round: 'quarter_final', nextRound: 'semi_final', label: 'Semi Finals' },
      { round: 'semi_final', nextRound: 'final', label: 'Final' }
    ];
    for (const { round, nextRound, label } of rounds) {
      const roundMatches = knockoutMatches.filter(m => m.round === round);
      const nextMatches = knockoutMatches.filter(m => m.round === nextRound);
      if (roundMatches.length === 0 || nextMatches.length === 0) continue;
      const isComplete = roundMatches.every(m =>
        ['completed', 'walkover', 'forfeit'].includes(m.status)
      );
      const nextRoundNeedsTeams = nextMatches.some(m => !m.entry1_id || !m.entry2_id);
      if (isComplete && nextRoundNeedsTeams) return { round, nextRound, label };
    }
    return null;
  }, [tournament?.tournament_matches]);

  const handleAdvanceWinners = async () => {
    try {
      setAdvancingWinners(true);
      setError(null);
      const advanced = await advanceWinnersToNextRound(tournamentId);
      await fetchTournamentData({ silent: true });
      if (!advanced) {
        setError('No rounds to advance. Ensure all matches in the previous round are complete with winners.');
      }
    } catch (err) {
      console.error('Error advancing winners:', err);
      setError(err.message);
    } finally {
      setAdvancingWinners(false);
    }
  };

  const handleAssignCourtToGroup = async (groupId, courtNumber) => {
    try {
      await assignCourtToGroup(groupId, courtNumber);
      setTournament(prev => ({
        ...prev,
        tournament_groups: (prev.tournament_groups || []).map(g =>
          g.id === groupId ? { ...g, assigned_court: courtNumber } : g
        )
      }));
    } catch (err) {
      console.error('Error assigning court:', err);
      setError(err.message);
    }
  };

  // Sort by match_order so display order stays fixed when scores are added (must be before early returns)
  const matches = useMemo(() => {
    const m = tournament?.tournament_matches || [];
    return [...m].sort((a, b) => (a.match_order ?? 0) - (b.match_order ?? 0));
  }, [tournament?.tournament_matches]);

  // Sort groups by sort_order for consistent display (Group A, B, C order)
  const sortedGroups = useMemo(() => {
    const g = tournament?.tournament_groups || [];
    return [...g].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [tournament?.tournament_groups]);

  const walkoverPointsFor = useMemo(
    () => getWalkoverPointsFor(normalizeScoringConfig(tournament)),
    [tournament?.points_target, tournament?.score_validation],
  );

  const groupsLayoutKey = useMemo(
    () =>
      sortedGroups
        .map(
          (g) =>
            `${g.id}:${(g.tournament_group_entries || [])
              .map((e) => e.entry_id)
              .sort()
              .join(',')}`
        )
        .join('|'),
    [sortedGroups]
  );

  const allGroupMatchesComplete = useMemo(() => {
    const groupMatches = (tournament?.tournament_matches || []).filter((m) => m.round === 'group');
    return (
      groupMatches.length > 0 &&
      groupMatches.every(
        (m) => ['walkover', 'forfeit'].includes(m.status) || isMatchEffectivelyComplete(m)
      )
    );
  }, [tournament?.tournament_matches]);

  const groupStageMatchesAll = useMemo(
    () => matches.filter((m) => m.round === 'group'),
    [matches]
  );
  const groupStageMatchesVisible = useMemo(
    () =>
      groupStageMatchesAll.filter(
        (m) => !hideCompletedGroupMatches || !isTournamentMatchResolved(m)
      ),
    [groupStageMatchesAll, hideCompletedGroupMatches]
  );

  const knockoutMatchesAll = useMemo(
    () => matches.filter((m) => m.round !== 'group'),
    [matches]
  );
  const knockoutMatchesVisible = useMemo(
    () =>
      knockoutMatchesAll.filter(
        (m) => !hideCompletedKnockoutMatches || !isTournamentMatchResolved(m)
      ),
    [knockoutMatchesAll, hideCompletedKnockoutMatches]
  );

  /** Group play is done but knockout not created yet — prompt host to open Knockout tab */
  const groupStageReadyForKnockout = useMemo(
    () =>
      Boolean(
        tournament?.status === 'group_stage' &&
          allGroupMatchesComplete &&
          !matches.some((m) => m.round !== 'group')
      ),
    [tournament?.status, allGroupMatchesComplete, matches]
  );

  const hasGroupMatches = useMemo(() => {
    const m = tournament?.tournament_matches || [];
    return m.some((x) => x.round === 'group');
  }, [tournament?.tournament_matches]);

  // If the active hub section is no longer available (e.g. groups cleared), move to a valid one
  useEffect(() => {
    if (!tournament) return;
    const teamsLen = tournament.tournament_entries?.length ?? 0;
    const groupsLen = tournament.tournament_groups?.length ?? 0;
    const canSetup = (tournament.status === 'draft' || groupsLen > 0) && teamsLen >= 4;
    const avail = [];
    if (canManageTeams) {
      if (canSetup || groupsLen > 0) avail.push('groups');
    }
    if (hasGroupMatches) avail.push('matches');
    if (groupStandings.length > 0) avail.push('standings');
    if (avail.length === 0) return;
    if (!avail.includes(groupHubSection)) {
      setGroupHubSection(
        groupStandings.length > 0
          ? 'standings'
          : hasGroupMatches
            ? 'matches'
            : canManageTeams && (canSetup || groupsLen > 0)
              ? 'groups'
              : 'matches'
      );
    }
  }, [tournament, hasGroupMatches, groupStandings.length, groupHubSection, canManageTeams]);

  // Players only use Matches and Standings — never land on host Groups hub
  useEffect(() => {
    if (canManageTeams) return;
    if (groupHubSection === 'groups') {
      if (groupStandings.length > 0) setGroupHubSection('standings');
      else if (hasGroupMatches) setGroupHubSection('matches');
      else setGroupHubSection('matches');
    }
  }, [canManageTeams, groupHubSection, hasGroupMatches, groupStandings.length]);

  /** Draft + no group rows yet: create default empty groups so hosts stay on one setup screen */
  useEffect(() => {
    if (!tournamentId || !tournament || !canManageTeams) return;
    if (activeTab !== 'group-stage' || groupHubSection !== 'groups') return;
    const teamCount = tournament.tournament_entries?.length ?? 0;
    if (teamCount < 4) return;
    const grp = tournament.tournament_groups || [];
    if (grp.length > 0) return;
    if (tournament.status !== 'draft') return;
    if (seedingDefaultGroupsInFlightRef.current) return;

    let cancelled = false;
    seedingDefaultGroupsInFlightRef.current = true;

    (async () => {
      try {
        const maxG = Math.max(2, Math.floor(teamCount / 2));
        const fromDb = tournament.group_count;
        const count = Math.min(Math.max(2, fromDb ?? configureGroupCount ?? 2), maxG);
        const teamsPerGroupStored = Math.floor(teamCount / count);
        await updateTournament(tournamentId, {
          group_count: count,
          teams_per_group: teamsPerGroupStored,
        });
        await syncTournamentGroups(tournamentId, count);
        if (cancelled) return;
        setConfigureGroupCount(count);
        await fetchTournamentData();
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        seedingDefaultGroupsInFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    tournamentId,
    tournament,
    canManageTeams,
    activeTab,
    groupHubSection,
    configureGroupCount,
  ]);

  const lastMainTabRef = useRef(activeTab);
  useEffect(() => {
    if (activeTab !== 'group-stage') {
      lastMainTabRef.current = activeTab;
      return;
    }
    if (lastMainTabRef.current !== 'group-stage') {
      if (canManageTeams) {
        if (groupStandings.length > 0) setGroupHubSection('standings');
        else if (hasGroupMatches) setGroupHubSection('matches');
        else setGroupHubSection('groups');
      } else {
        if (groupStandings.length > 0) setGroupHubSection('standings');
        else if (hasGroupMatches) setGroupHubSection('matches');
        else setGroupHubSection('matches');
      }
    }
    lastMainTabRef.current = activeTab;
  }, [activeTab, hasGroupMatches, tournament?.tournament_groups?.length, canManageTeams, groupStandings.length]);

  const goToGroupStage = (section) => {
    setActiveTab('group-stage');
    if (section) setGroupHubSection(section);
  };

  // Hide Group stage tab when nothing to show; avoid empty hub
  useEffect(() => {
    if (!tournament || loading) return;
    const teamsLen = tournament.tournament_entries?.length ?? 0;
    const groupsLen = tournament.tournament_groups?.length ?? 0;
    const hm = (tournament.tournament_matches || []).some((m) => m.round === 'group');
    const hubVisible =
      ((tournament.status === 'draft' || groupsLen > 0) && teamsLen >= 4) || groupsLen > 0 || hm;
    if (activeTab === 'group-stage' && !hubVisible) {
      setActiveTab('overview');
    }
  }, [tournament, loading, activeTab]);

  useEffect(() => {
    if (
      activeTab === 'settings' &&
      tournament &&
      (tournament.status === 'completed' || tournament.status === 'cancelled')
    ) {
      setActiveTab('overview');
    }
  }, [tournament?.status, tournament, activeTab]);

  useEffect(() => {
    if (activeTab === 'settings' && !canManageTeams) {
      setActiveTab('overview');
    }
  }, [activeTab, canManageTeams]);

  if (loading) {
    return (
      <div className={TOURNAMENT_DETAIL_PAGE_SHELL}>
        <div className={TOURNAMENT_DETAIL_PAGE_INNER}>
          <Typography variant="body" className="text-center">Loading tournament...</Typography>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className={TOURNAMENT_DETAIL_PAGE_SHELL}>
        <div className={TOURNAMENT_DETAIL_PAGE_INNER}>
          <Card variant="danger" size="medium">
            <Typography variant="body">Tournament not found</Typography>
          </Card>
        </div>
      </div>
    );
  }

  const teams = (tournament.tournament_entries || []).map(entryToLegacyTeamShape).filter(Boolean);
  const groups = tournament.tournament_groups || [];
  const hasGroupAssignments = groups.some((g) => (g.tournament_group_entries?.length ?? 0) > 0);
  const groupPlayStarted = groupStageHasRecordedPlay(matches);
  const singlesTournament = isSinglesTournament(tournament);
  const maxConfigureGroups = Math.max(2, Math.floor(teams.length / 2));
  const showGroupHubSetup =
    (tournament.status === 'draft' || groups.length > 0) && teams.length >= 4;
  const showGroupHubTab =
    showGroupHubSetup || groups.length > 0 || hasGroupMatches;

  const isPlayerView = !canManageTeams;
  const showTournamentSettingsTab =
    canManageTeams &&
    tournament.status !== 'completed' &&
    tournament.status !== 'cancelled';

  // Overview tab computed values
  const currentStep = getCurrentTournamentStep(tournament, teams, groups, matches);
  const tournamentSteps = getTournamentSteps(tournament, teams, groups, matches);
  const actionItems = getTournamentActionItems(tournament, teams, groups, matches, groupStandings);
  const statistics = getTournamentStatistics(tournament, teams, matches);
  const userProgress = getUserTournamentProgress(currentUser?.id, teams, matches, groupStandings);

  const handleStepNavigation = (action) => {
    if (action === 'teams') {
      setActiveTab('teams');
    } else if (action === 'group-stage') {
      goToGroupStage('groups');
    } else if (action === 'knockout') {
      setActiveTab('knockout');
    }
  };

  const hasKnockoutContent = knockoutMatchesAll.length > 0;
  return (
    <div className={TOURNAMENT_DETAIL_PAGE_SHELL}>
      <div className={TOURNAMENT_DETAIL_PAGE_INNER}>
        <div className="mb-4 sm:mb-6">
          <BackButton hideOnDesktop className="mb-2" onClick={() => navigate(`/club/${clubId}/tournaments`)}>
            Back to Tournaments
          </BackButton>
        </div>

        <header className="mb-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Heading
              level={1}
              className="text-xl leading-tight sm:text-2xl md:text-3xl"
            >
              <span className="mr-1.5" aria-hidden>🏆</span>
              <span className="break-words [overflow-wrap:anywhere]">{tournament.name}</span>
            </Heading>
            <StatusBadge
              variant={getTournamentStatusVariant(tournament.status)}
              size="medium"
            >
              {getTournamentStatusLabel(tournament.status)}
            </StatusBadge>
          </div>
          <Text className="mt-1.5 text-sm text-text-secondary">
            {tournament.game_type?.name ? (
              <span className="text-text-primary/90">{tournament.game_type.name}</span>
            ) : (
              <span className="text-text-muted">Format</span>
            )}
            {tournament.tournament_date ? (
              <>
                <span className="text-text-muted"> · </span>
                <span>
                  {(() => {
                    const [year, month, day] = tournament.tournament_date.split('-').map(Number);
                    return year && month && day
                      ? format(new Date(year, month - 1, day), 'EEE, d MMM yyyy')
                      : tournament.tournament_date;
                  })()}
                </span>
              </>
            ) : null}
            {tournament.start_time ? (
              <>
                <span className="text-text-muted"> · </span>
                <span>
                  {String(tournament.start_time).slice(0, 5)}
                  {tournament.end_time ? `–${String(tournament.end_time).slice(0, 5)}` : ''}
                </span>
              </>
            ) : null}
            {tournament.location ? (
              <>
                <span className="text-text-muted"> · </span>
                <span>{tournament.location}</span>
              </>
            ) : null}
            <span className="text-text-muted"> · </span>
            {getTournamentStatusDescription(tournament.status)}
          </Text>
          {tournament.status === 'completed' && tournament.winning_team && (
            <Text className="mt-1.5 text-sm font-medium text-success">
              Champion: {formatTournamentEntryLabel(tournament.winning_team, singlesTournament)}
            </Text>
          )}
          <p
            className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-text-muted"
            aria-label="Tournament summary"
          >
            <span>
              <span className="sr-only">Format: </span>
              Bo{tournament.default_best_of}
            </span>
            <span aria-hidden className="text-text-muted/60">·</span>
            <span>
              <span className="sr-only">Courts: </span>
              {tournament.number_of_courts || 2} courts
            </span>
            <span aria-hidden className="text-text-muted/60">·</span>
            <span>
              <span className="sr-only">{singlesTournament ? 'Players' : 'Teams'}: </span>
              {teams.length} {singlesTournament ? 'players' : 'teams'}
            </span>
            <span aria-hidden className="text-text-muted/60">·</span>
            <span>
              <span className="sr-only">Groups: </span>
              {groups.length ? `${groups.length} groups` : 'No groups'}
            </span>
          </p>
        </header>

        {error && (
          <Card variant="danger" size="small" className="mb-6">
            <Typography variant="body">{error}</Typography>
            <Button
              variant="secondary"
              size="small"
              onClick={() => setError(null)}
              className="mt-2"
            >
              Dismiss
            </Button>
          </Card>
        )}

        {/* Tabs */}
        <Tabs
          tabs={[
            {
              id: 'overview',
              label: 'Overview',
              content: (
                <div className="space-y-6">
                  {/* Player/Member View */}
                  {isPlayerView && (
                    <>
                      {/* Tournament Timeline */}
                      <Card variant="default" size="large">
                        <Heading level={3} className="mb-6 text-lg sm:text-xl">
                          Tournament Progress
                        </Heading>
                        <TournamentTimeline tournament={tournament} matches={matches} />
                      </Card>

                      {/* My Progress Card (if participating) */}
                      {userProgress && (
                        <MyTournamentProgress
                          userProgress={userProgress}
                          isSingles={singlesTournament}
                          onViewStandings={() => goToGroupStage('standings')}
                          onViewMatches={() => goToGroupStage('matches')}
                        />
                      )}

                      {/* Tournament Highlights */}
                      {tournament.status !== 'draft' && tournament.status !== 'cancelled' && (
                        <TournamentHighlights
                          tournament={tournament}
                          groupStandings={groupStandings}
                          matches={matches}
                          onViewGroup={() => goToGroupStage('standings')}
                          onViewKnockout={() => setActiveTab('knockout')}
                        />
                      )}

                      {/* Quick Navigation for Draft/Registration */}
                      {(tournament.status === 'draft' || tournament.status === 'registration') && (
                        <Card variant="muted" size="medium" className="text-center">
                          <div className="text-4xl mb-3">🏆</div>
                          <Heading level={4} className="mb-2">
                            {tournament.status === 'draft' ? 'Tournament Setup in Progress' : 'Registration Open'}
                          </Heading>
                          <Text className="text-text-secondary mb-4">
                            {tournament.status === 'draft'
                              ? 'The tournament is being set up. Check back soon!'
                              : 'Teams are being registered. View the team list to see who\'s participating.'}
                          </Text>
                          <Button
                            variant="primary"
                            size="small"
                            onClick={() => setActiveTab('teams')}
                          >
                            View {singlesTournament ? 'Players' : 'Teams'}
                          </Button>
                        </Card>
                      )}

                      {/* Champions Display for Completed Tournament */}
                      {tournament.status === 'completed' && tournament.winning_team && (
                        <Card variant="success" size="large" elevated className="text-center border-2 border-success">
                          <div className="text-6xl mb-4">🏆</div>
                          <Heading level={3} className="mb-2 text-xl sm:text-2xl">
                            Champions!
                          </Heading>
                          <Typography variant="strong" className="text-lg text-success-dark mb-4">
                            {formatTournamentEntryLabel(tournament.winning_team, singlesTournament)}
                          </Typography>
                          <div className="flex flex-wrap gap-2 justify-center">
                            <Button
                              variant="success"
                              size="small"
                              onClick={() => setActiveTab('knockout')}
                            >
                              View Bracket
                            </Button>
                            {groupStandings.length > 0 && (
                              <Button
                                variant="secondary"
                                size="small"
                                onClick={() => goToGroupStage('standings')}
                              >
                                View Standings
                              </Button>
                            )}
                          </div>
                        </Card>
                      )}
                    </>
                  )}

                  {/* Host View */}
                  {!isPlayerView && (
                    <>
                      {/* Progress Stepper */}
                      <Card variant="default" size="large">
                        <Heading level={3} className="mb-6 text-lg sm:text-xl">
                          Tournament Setup
                        </Heading>
                        <TournamentProgressStepper
                          steps={tournamentSteps}
                          currentStep={currentStep}
                          onStepClick={handleStepNavigation}
                          interactive={true}
                          orientation={window.innerWidth < 640 ? 'vertical' : 'horizontal'}
                        />
                      </Card>

                      {/* Action Dashboard */}
                      <ActionDashboard
                        actionItems={actionItems}
                        onActionClick={handleStepNavigation}
                      />

                      {/* Quick Actions for Draft Status */}
                      {tournament.status === 'draft' && teams.length >= 4 && groups.length === 0 && (
                        <Card variant="info" size="medium">
                          <Heading level={4} className="mb-3 text-base sm:text-lg">
                            Ready to set up groups
                          </Heading>
                          <Text className="text-text-secondary mb-4">
                            You have {teams.length} {singlesTournament ? 'players' : 'teams'} registered. Open the Group
                            stage tab to create empty groups. Use <strong>Auto-assign teams</strong> for an even split,
                            then <strong>Randomise groups</strong> to reshuffle until the draw looks fair, or drag
                            players by hand.
                          </Text>
                          <Button
                            variant="primary"
                            size="small"
                            type="button"
                            onClick={() => goToGroupStage('groups')}
                            className="w-full sm:w-auto"
                          >
                            Open group setup
                          </Button>
                        </Card>
                      )}

                      {/* Champions Display for Completed Tournament */}
                      {tournament.status === 'completed' && tournament.winning_team && (
                        <Card variant="success" size="large" elevated className="text-center border-2 border-success">
                          <div className="text-6xl mb-4">🎉</div>
                          <Heading level={3} className="mb-2 text-xl sm:text-2xl">
                            Tournament Complete!
                          </Heading>
                          <Typography variant="strong" className="text-lg text-success-dark mb-4">
                            Champions: {formatTournamentEntryLabel(tournament.winning_team, singlesTournament)}
                          </Typography>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <div className="p-3 bg-surface/50 rounded-lg">
                              <div className="text-2xl font-bold text-primary mb-1">
                                {teams.length}
                              </div>
                              <Text className="text-xs text-text-muted">Teams</Text>
                            </div>
                            <div className="p-3 bg-surface/50 rounded-lg">
                              <div className="text-2xl font-bold text-primary mb-1">
                                {matches.length}
                              </div>
                              <Text className="text-xs text-text-muted">Matches</Text>
                            </div>
                            <div className="p-3 bg-surface/50 rounded-lg">
                              <div className="text-2xl font-bold text-primary mb-1">
                                {statistics.totalGamesPlayed}
                              </div>
                              <Text className="text-xs text-text-muted">Games</Text>
                            </div>
                            <div className="p-3 bg-surface/50 rounded-lg">
                              <div className="text-2xl font-bold text-success mb-1">
                                100%
                              </div>
                              <Text className="text-xs text-text-muted">Complete</Text>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 justify-center">
                            <Button
                              variant="success"
                              size="small"
                              onClick={() => setActiveTab('knockout')}
                            >
                              View Bracket
                            </Button>
                            {groupStandings.length > 0 && (
                              <Button
                                variant="secondary"
                                size="small"
                                onClick={() => goToGroupStage('standings')}
                              >
                                View Standings
                              </Button>
                            )}
                          </div>
                        </Card>
                      )}
                    </>
                  )}
                </div>
              )
            },
            {
              id: 'teams',
              label: singlesTournament ? 'Players' : 'Teams',
              badge: teams.length,
              content: (
                <div className="space-y-4">
                  <AddTeamSection
                    players={players}
                    teams={teams}
                    isSingles={singlesTournament}
                    soloEntrants={tournament?.tournament_solo_entrants || []}
                    canManageTeams={canManageTeams}
                    hasGroupsConfigured={(tournament?.tournament_groups?.length ?? 0) > 0}
                    onAddTeam={singlesTournament ? undefined : handleAddTeam}
                    onAddSinglesEntry={singlesTournament && canManageTeams ? handleAddSinglesEntry : undefined}
                    onRemoveTeam={canManageTeams ? handleRemoveTeamClick : undefined}
                    onWithdrawTeam={canManageTeams ? (id) => setTeamToWithdrawId(id) : undefined}
                    onUndoWithdrawTeam={canManageTeams ? (id) => setTeamToUnwithdrawId(id) : undefined}
                    onRegisterSolo={!singlesTournament && canManageTeams ? handleRegisterSolo : undefined}
                    onRemoveSolo={!singlesTournament && canManageTeams ? handleRemoveSolo : undefined}
                    onPairSolos={!singlesTournament && canManageTeams ? handlePairSolos : undefined}
                    onGuestAdded={handleGuestAdded}
                    onGuestUpdated={handleGuestUpdated}
                    clubId={clubId}
                    currentUser={currentUser}
                    seasonId={tournament?.season_id}
                    tournamentStatus={tournament?.status}
                  />
                  {!singlesTournament && (
                    <TournamentPairingSection
                      tournament={tournament}
                      tournamentId={tournamentId}
                      players={players}
                      canManageTeams={canManageTeams}
                      onRefresh={() => fetchTournamentData({ silent: true })}
                    />
                  )}
                </div>
              )
            },
            ...(showGroupHubTab ? [{
              id: 'group-stage',
              label: 'Group stage',
              badge: hasGroupMatches ? matches.filter(m => m.round === 'group').length : undefined,
              content: (
                <div className="space-y-6">
                  <div
                    className="flex flex-wrap gap-2 border-b border-border-subtle pb-3"
                    role="tablist"
                    aria-label="Group stage sections"
                  >
                    {canManageTeams && (showGroupHubSetup || groups.length > 0) && (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={groupHubSection === 'groups'}
                        onClick={() => setGroupHubSection('groups')}
                        className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                          groupHubSection === 'groups'
                            ? 'border-primary bg-primary text-text-inverse'
                            : 'border-border-medium bg-surface text-text-secondary hover:bg-surface-subtle'
                        }`}
                      >
                        Groups
                      </button>
                    )}
                    {groupStandings.length > 0 && (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={groupHubSection === 'standings'}
                        onClick={() => setGroupHubSection('standings')}
                        className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                          groupHubSection === 'standings'
                            ? 'border-primary bg-primary text-text-inverse'
                            : 'border-border-medium bg-surface text-text-secondary hover:bg-surface-subtle'
                        }`}
                      >
                        Standings
                      </button>
                    )}
                    {hasGroupMatches && (
                      <button
                        type="button"
                        role="tab"
                        aria-selected={groupHubSection === 'matches'}
                        onClick={() => setGroupHubSection('matches')}
                        className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                          groupHubSection === 'matches'
                            ? 'border-primary bg-primary text-text-inverse'
                            : 'border-border-medium bg-surface text-text-secondary hover:bg-surface-subtle'
                        }`}
                      >
                        Matches
                      </button>
                    )}
                  </div>

                  {isPlayerView && !hasGroupMatches && groupStandings.length === 0 && (
                    <Card variant="muted" size="small" className="!p-3">
                      <Text className="text-text-secondary text-sm">
                        Group fixtures and standings will show here when the host has generated them.
                      </Text>
                    </Card>
                  )}

                  {groupStageReadyForKnockout && (
                    <Card
                      variant="default"
                      size="medium"
                      className="border-2 border-primary/60 bg-gradient-to-br from-primary/10 via-surface to-surface shadow-md"
                      role="status"
                      aria-live="polite"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex gap-3 min-w-0">
                          <div
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/20 text-lg"
                            aria-hidden
                          >
                            🏆
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <StatusBadge variant="success" size="small">
                                Group stage complete
                              </StatusBadge>
                            </div>
                            <Heading level={4} className="mb-1 text-base sm:text-lg">
                              {isPlayerView ? 'Knockout is next' : 'Next: open the Knockout tab'}
                            </Heading>
                            <Text className="text-text-secondary text-sm">
                              {isPlayerView
                                ? 'All group matches are finished. The tournament host will set up the knockout from the Knockout tab. You can open it here to follow along once the bracket exists.'
                                : 'All group matches are finished. Go to Knockout to pick qualifiers and generate the bracket.'}
                            </Text>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="primary"
                          size="medium"
                          className="shrink-0 w-full sm:w-auto"
                          onClick={() => setActiveTab('knockout')}
                        >
                          Go to Knockout
                        </Button>
                      </div>
                    </Card>
                  )}

                  {groupHubSection === 'groups' && canManageTeams && (
                    <div className="space-y-6">
                      {showGroupHubSetup && (
                        <>
                          {groups.length === 0 ? (
                            <Card variant="muted" size="medium">
                              <Text className="text-sm text-text-secondary">
                                Creating default groups…
                              </Text>
                            </Card>
                          ) : (
                            <>
                              {tournament.status === 'draft' &&
                                groups.length > 0 &&
                                groups.every((g) => !(g.tournament_group_entries?.length > 0)) && (
                                  <Card variant="info" size="small">
                                    <Text className="text-sm text-text-secondary">
                                      Teams are listed below in the unassigned pool. Use{' '}
                                      <strong>Auto-assign teams</strong> for a predictable even split, or drag them into
                                      place. After anyone is in a group, that button becomes{' '}
                                      <strong>Randomise groups</strong> so you can reshuffle until the draw looks fair.
                                      Save when each group has at least two teams, then use{' '}
                                      <strong>Generate matches</strong> when you&apos;re ready.
                                    </Text>
                                  </Card>
                                )}
                              <Card variant="default" size="large">
                                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between xl:gap-4">
                                  <Heading level={3} className="mb-0 shrink-0">
                                    Groups
                                  </Heading>
                                  {tournament.status !== 'completed' && tournament.status !== 'cancelled' && (
                                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 gap-y-2">
                                      <div className="flex items-center gap-2">
                                        <Text className="text-text-secondary text-sm">Group stage:</Text>
                                        <select
                                          value={getBestOfForRound(tournament, 'group')}
                                          onChange={(e) => handleUpdateGroupFormat(e.target.value)}
                                          className="rounded-md border border-border-medium bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                        >
                                          <option value={1}>Best of 1</option>
                                          <option value={3}>Best of 3</option>
                                          <option value={5}>Best of 5</option>
                                        </select>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <label
                                          htmlFor="tournament-inline-group-count"
                                          className="text-text-secondary text-sm"
                                        >
                                          Number of groups:
                                        </label>
                                        <select
                                          id="tournament-inline-group-count"
                                          value={groups.length}
                                          disabled={applyingGroupCount}
                                          onChange={handleGroupsCountSelectChange}
                                          className="min-h-[36px] rounded-md border border-border-medium bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                                          aria-label="Number of groups"
                                        >
                                          {Array.from({ length: maxConfigureGroups - 1 }, (_, i) => i + 2).map((n) => (
                                            <option key={n} value={n}>
                                              {n}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <label
                                          htmlFor="tournament-inline-court-count"
                                          className="text-text-secondary text-sm"
                                        >
                                          Courts:
                                        </label>
                                        <select
                                          id="tournament-inline-court-count"
                                          value={Math.min(
                                            10,
                                            Math.max(1, tournament.number_of_courts || 2)
                                          )}
                                          onChange={handleUpdateNumberOfCourts}
                                          className="min-h-[36px] rounded-md border border-border-medium bg-surface px-2 py-1.5 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                          aria-label="Number of courts at the venue"
                                          title="How many courts are available for scheduling. Matches already created keep their court numbers until you regenerate group matches."
                                        >
                                          {Array.from({ length: 10 }, (_, i) => i + 1).map((c) => (
                                            <option key={c} value={c}>
                                              {c}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <Button
                                        variant="success"
                                        size="small"
                                        type="button"
                                        title={
                                          groupPlayStarted
                                            ? 'Group matches have started or scores exist. Change assignments manually (Edit below) if the draw must change.'
                                            : hasGroupAssignments
                                              ? 'Shuffle everyone into a new random balanced split. Click again for another draw.'
                                              : 'Put every team into groups in an even split (stable order by signup time).'
                                        }
                                        onClick={() =>
                                          handleAutoAssignTeams(hasGroupAssignments ? 'random' : 'balanced')
                                        }
                                        disabled={teams.length < 4 || groupPlayStarted}
                                      >
                                        {hasGroupAssignments ? 'Randomise groups' : 'Auto-assign teams'}
                                      </Button>
                                      <div className="flex flex-wrap gap-2">
                                        {matches.some((m) => m.round === 'group') ? (
                                          <Button
                                            variant="warning"
                                            size="small"
                                            type="button"
                                            onClick={() => setShowRegenerateConfirm(true)}
                                          >
                                            🔄 Regenerate group matches
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="success"
                                            size="small"
                                            type="button"
                                            onClick={handleRegenerateMatches}
                                          >
                                            Generate matches
                                          </Button>
                                        )}
                                      </div>
                                      <Button
                                        variant="danger"
                                        size="small"
                                        type="button"
                                        onClick={() => setShowClearGroupsConfirm(true)}
                                      >
                                        Clear all groups
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </Card>
                              <EditGroupsSection
                                key={groupsLayoutKey}
                                tournament={tournament}
                                teams={teams}
                                groups={sortedGroups}
                                onSaveChanges={handleUpdateGroupAssignments}
                                numberOfCourts={tournament.number_of_courts || 1}
                                onAssignCourtToGroup={handleAssignCourtToGroup}
                              />
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {groupHubSection === 'matches' && hasGroupMatches && (
                <div className="space-y-6">
                  {/* Group Stage Matches - primary action for hosts */}
                  <Card variant="default" size="large">
                    <div className="mb-4 space-y-3">
                      <Heading level={3} className="mb-0">Group Stage Matches</Heading>
                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                        <div
                          className="grid w-full grid-cols-2 rounded-md border border-border-medium overflow-hidden sm:flex sm:w-auto"
                          role="group"
                          aria-label="Match list view"
                        >
                          <button
                            type="button"
                            onClick={() => setGroupStageView('group')}
                            className={`min-h-[44px] px-3 py-2 text-sm font-medium transition-colors sm:py-1.5 ${
                              groupStageView === 'group'
                                ? 'bg-primary text-text-inverse'
                                : 'bg-surface text-text-secondary hover:bg-surface-subtle'
                            }`}
                          >
                            <span className="sm:hidden">By group</span>
                            <span className="hidden sm:inline">Group view</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setGroupStageView('court')}
                            className={`min-h-[44px] border-l border-border-medium px-3 py-2 text-sm font-medium transition-colors sm:border-l-0 sm:py-1.5 ${
                              groupStageView === 'court'
                                ? 'bg-primary text-text-inverse'
                                : 'bg-surface text-text-secondary hover:bg-surface-subtle'
                            }`}
                          >
                            <span className="sm:hidden">By court</span>
                            <span className="hidden sm:inline">Court view</span>
                          </button>
                        </div>
                        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
                          <label
                            className="flex min-h-[44px] shrink-0 cursor-pointer items-center gap-2 text-sm text-text-secondary select-none"
                            title="Hide finished matches in this list"
                          >
                            <input
                              type="checkbox"
                              checked={hideCompletedGroupMatches}
                              onChange={(e) => setHideCompletedGroupMatches(e.target.checked)}
                              className="h-4 w-4 rounded border-border-medium text-primary focus:ring-primary"
                            />
                            <span className="whitespace-nowrap">Hide completed</span>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {groupStageMatchesAll.length > 0 && groupStageMatchesVisible.length === 0 ? (
                        <Text className="text-text-secondary text-sm">
                          All group matches are completed. Uncheck Hide completed to review results.
                        </Text>
                      ) : groupStageView === 'group' ? (
                        sortedGroups
                          .filter((g) => groupStageMatchesVisible.some((m) => m.group_id === g.id))
                          .map((group) => {
                            const groupMatches = groupStageMatchesAll
                              .filter((m) => m.group_id === group.id)
                              .sort((a, b) => (a.match_order ?? 0) - (b.match_order ?? 0));
                            const visibleMatches = groupMatches.filter(
                              (m) => !hideCompletedGroupMatches || !isTournamentMatchResolved(m)
                            );
                            const playedCount = groupMatches.filter(isTournamentMatchResolved).length;
                            return (
                              <GroupMatchSection
                                key={group.id}
                                groupId={group.id}
                                groupName={group.name}
                                matches={visibleMatches}
                                playedCount={playedCount}
                                totalCount={groupMatches.length}
                                isSingles={singlesTournament}
                                canManage={canManageTeams}
                                onOpenScore={handleOpenScoreModal}
                              />
                            );
                          })
                      ) : (
                        (() => {
                          const byCourt = {};
                          groupStageMatchesAll.forEach((m) => {
                            const court = m.court_number ?? 'unassigned';
                            if (!byCourt[court]) byCourt[court] = [];
                            byCourt[court].push(m);
                          });
                          const courtNumbers = Object.keys(byCourt)
                            .filter((k) => k !== 'unassigned')
                            .map(Number)
                            .sort((a, b) => a - b);
                          if (byCourt.unassigned?.length) courtNumbers.push('unassigned');
                          return courtNumbers.map((courtKey) => {
                            const courtMatches = byCourt[courtKey].sort(
                              (a, b) =>
                                (a.court_match_order ?? a.match_order ?? 0) -
                                (b.court_match_order ?? b.match_order ?? 0)
                            );
                            const visibleMatches = courtMatches.filter(
                              (m) => !hideCompletedGroupMatches || !isTournamentMatchResolved(m)
                            );
                            if (visibleMatches.length === 0) return null;
                            const playedCount = courtMatches.filter(isTournamentMatchResolved).length;
                            const label = courtKey === 'unassigned' ? 'Unassigned' : `Court ${courtKey}`;
                            return (
                              <GroupMatchSection
                                key={courtKey}
                                groupId={`court-${courtKey}`}
                                title={label}
                                matches={visibleMatches}
                                playedCount={playedCount}
                                totalCount={courtMatches.length}
                                isSingles={singlesTournament}
                                canManage={canManageTeams}
                                onOpenScore={handleOpenScoreModal}
                                metaMode="court"
                              />
                            );
                          });
                        })()
                      )}
                    </div>
                  </Card>

                </div>
                  )}

                  {groupHubSection === 'standings' && groupStandings.length > 0 && (
                    <Card id="group-standings" variant="default" size="large" className="scroll-mt-4">
                      <GroupStandingsTables
                        groupStandings={groupStandings}
                        teamsQualifyingPerGroup={tournament.progression_top_n ?? 2}
                        rankingOrder={tournament.ranking_order}
                        tiebreakerOrder={tournament.tiebreaker_order}
                        isSinglesTournament={singlesTournament}
                        walkoverPointsFor={walkoverPointsFor}
                      />
                    </Card>
                  )}
                </div>
              )
            }] : []),
            {
              id: 'knockout',
              label: 'Knockout',
              badge: matches.some(m => m.round !== 'group') ? matches.filter(m => m.round !== 'group').length : undefined,
              content: matches.some(m => m.round !== 'group') ? (
                <Card variant="default" size="large">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <Heading level={3}>Knockout Stage</Heading>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      {canManageTeams && roundNeedsAdvancing && (
                        <Button
                          type="button"
                          variant="primary"
                          size="medium"
                          onClick={handleAdvanceWinners}
                          disabled={advancingWinners}
                        >
                          {advancingWinners ? 'Advancing...' : `Advance to ${roundNeedsAdvancing.label}`}
                        </Button>
                      )}
                      {canManageTeams &&
                        tournament.status !== 'completed' &&
                        tournament.status !== 'cancelled' &&
                        matches.some((m) => m.round !== 'group') && (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => setShowEditMatchupsModal(true)}
                        >
                          Edit Matchups
                        </Button>
                      )}
                      <div className="flex rounded-lg border border-border-medium p-0.5">
                        <button
                          type="button"
                          onClick={() => setKnockoutView('list')}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            knockoutView === 'list'
                              ? 'bg-primary text-white'
                              : 'text-text-secondary hover:text-text-primary'
                          }`}
                        >
                          List
                        </button>
                        <button
                          type="button"
                          onClick={() => setKnockoutView('bracket')}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                            knockoutView === 'bracket'
                              ? 'bg-primary text-white'
                              : 'text-text-secondary hover:text-text-primary'
                          }`}
                        >
                          Bracket
                        </button>
                      </div>
                      {knockoutView === 'list' && (
                        <label
                          className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer shrink-0 select-none sm:pl-2 sm:ml-0.5 sm:border-l sm:border-border-subtle"
                          title="Hide finished matches in this list"
                        >
                          <input
                            type="checkbox"
                            checked={hideCompletedKnockoutMatches}
                            onChange={(e) => setHideCompletedKnockoutMatches(e.target.checked)}
                            className="rounded border-border-medium text-primary focus:ring-primary"
                          />
                          <span>Hide completed</span>
                        </label>
                      )}
                    </div>
                  </div>
                  {knockoutView === 'bracket' ? (
                    <KnockoutBracketTree
                      matches={matches.filter(m => m.round !== 'group')}
                      allMatches={matches}
                      onEnterScore={handleOpenScoreModal}
                      onEditScore={handleOpenScoreModal}
                      readOnly={!canManageTeams}
                    />
                  ) : (
                  <div className="space-y-4">
                    {knockoutMatchesAll.length > 0 && knockoutMatchesVisible.length === 0 ? (
                      <Text className="text-text-secondary text-sm">
                        All knockout matches are completed. Uncheck Hide completed to review results.
                      </Text>
                    ) : (
                    [
                      { key: 'round_of_16', label: 'Round of 16' },
                      { key: 'quarter_final', label: 'Quarter Finals' },
                      { key: 'semi_final', label: 'Semi Finals' },
                      { key: 'bronze', label: '3rd Place Match' },
                      { key: 'final', label: 'Final' }
                    ].map(({ key, label }) => {
                      const roundMatches = knockoutMatchesVisible.filter((m) => m.round === key);
                      if (roundMatches.length === 0) return null;
                      return (
                        <div key={key}>
                          <Typography variant="strong" className="mb-2 block">{label}</Typography>
                          <div className="space-y-3">
                            {roundMatches.map((match) => {
                              const [team1Seed, team2Seed] = getTeamSeedsForMatch(match, matches);
                              return (
                                <TournamentMatchCard
                                  key={match.id}
                                  match={match}
                                  isSingles={singlesTournament}
                                  canManage={canManageTeams}
                                  onOpenScore={handleOpenScoreModal}
                                  metaMode="knockout"
                                  team1Seed={team1Seed}
                                  team2Seed={team2Seed}
                                  isFinalRound={match.round === 'final'}
                                />
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                  </div>
                  )}
                </Card>
              ) : (
                <Card variant="default" size="large">
                  <Heading level={3} className="mb-4">Knockout Stage</Heading>
                  {tournament.status === 'group_stage' && allGroupMatchesComplete ? (
                    isPlayerView ? (
                      <Text className="text-text-secondary block">
                        Group stage is complete. The tournament host will create the knockout bracket when ready; check
                        back here to follow the draw.
                      </Text>
                    ) : (
                      <>
                        <Text className="text-text-secondary mb-4 block">
                          The group stage is complete. Select which teams qualify for the knockout stage.
                        </Text>
                        <Text className="text-text-secondary text-sm mb-4 block">
                          Suggested qualifiers follow the same ordering as group standings; resolve any ties on the
                          cut line before locking the bracket.
                        </Text>
                        <Button
                          variant="primary"
                          size="medium"
                          onClick={() => setShowGenerateKnockoutModal(true)}
                        >
                          Generate Knockout Bracket
                        </Button>
                      </>
                    )
                  ) : isPlayerView ? (
                    <>
                      <Text className="text-text-secondary mb-4 block">
                        {(() => {
                          if (!groups.length) {
                            return 'The knockout will appear here after the host has set up groups and fixtures.';
                          }
                          if (!matches.some((m) => m.round === 'group')) {
                            return 'The knockout will appear after group matches are generated and played.';
                          }
                          return 'The knockout is created by the host after all group results are in.';
                        })()}
                      </Text>
                      {groups.length > 0 &&
                        matches.some((m) => m.round === 'group') &&
                        (() => {
                          const incompleteGroupMatches = matches.filter(
                            (m) =>
                              m.round === 'group' &&
                              !['walkover', 'forfeit'].includes(m.status) &&
                              !isMatchEffectivelyComplete(m)
                          );
                          if (incompleteGroupMatches.length === 0) return null;
                          const formatTeam = (t) =>
                            t ? formatTournamentEntryLabel(t, singlesTournament) : 'TBD';
                          return (
                            <div className="mb-4 p-4 rounded-lg bg-warning/10 border border-warning/30">
                              <Typography variant="strong" className="text-warning text-sm mb-2 block">
                                {incompleteGroupMatches.length} match{incompleteGroupMatches.length !== 1 ? 'es' : ''} still in progress:
                              </Typography>
                              <ul className="list-disc list-inside space-y-1 text-sm text-text-secondary">
                                {incompleteGroupMatches.map((m) => (
                                  <li key={m.id}>
                                    {m.group?.name ? `Group ${m.group.name}: ` : ''}
                                    {formatTeam(m.team1)} vs {formatTeam(m.team2)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })()}
                    </>
                  ) : (
                    <>
                      <Text className="text-text-secondary mb-4 block">
                        {!groups.length
                          ? 'Add teams, configure groups, and generate group matches first.'
                            : !matches.some((m) => m.round === 'group')
                            ? 'Generate group matches from the Group stage tab (Rosters), then complete them.'
                            : 'Complete all group stage matches before generating the knockout bracket.'}
                      </Text>
                      {groups.length > 0 &&
                        matches.some((m) => m.round === 'group') &&
                        (() => {
                          const incompleteGroupMatches = matches.filter(
                            (m) =>
                              m.round === 'group' &&
                              !['walkover', 'forfeit'].includes(m.status) &&
                              !isMatchEffectivelyComplete(m)
                          );
                          if (incompleteGroupMatches.length === 0) return null;
                          const formatTeam = (t) =>
                            t ? formatTournamentEntryLabel(t, singlesTournament) : 'TBD';
                          return (
                            <div className="mb-4 p-4 rounded-lg bg-warning/10 border border-warning/30">
                              <Typography variant="strong" className="text-warning text-sm mb-2 block">
                                {incompleteGroupMatches.length} match{incompleteGroupMatches.length !== 1 ? 'es' : ''} still to complete:
                              </Typography>
                              <ul className="list-disc list-inside space-y-1 text-sm text-text-secondary">
                                {incompleteGroupMatches.map((m) => (
                                  <li key={m.id}>
                                    {m.group?.name ? `Group ${m.group.name}: ` : ''}
                                    {formatTeam(m.team1)} vs {formatTeam(m.team2)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })()}
                      <Button
                        variant="primary"
                        size="medium"
                        disabled
                        className="opacity-60 cursor-not-allowed"
                      >
                        Generate Knockout Bracket
                      </Button>
                    </>
                  )}
                </Card>
              )
            },
            ...(showTournamentSettingsTab
              ? [
                  {
                    id: 'settings',
                    label: 'Settings',
                    icon: '⚙️',
                    leadingSeparator: true,
                    content: (
                      <EditTournamentSettingsPanel
                        tournament={tournament}
                        onSave={handleSaveTournamentSettings}
                      />
                    )
                  }
                ]
              : [])
          ]}
          defaultTab="overview"
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Score Modal */}
        <TournamentMatchScoreModal
          isOpen={showScoreModal}
          onClose={() => {
            setShowScoreModal(false);
            setSelectedMatch(null);
          }}
          match={selectedMatch}
          tournament={tournament}
          onSaveScore={handleSaveMatchScore}
          onMatchFormatChanged={async (matchId) => {
            const data = await fetchTournamentData({ silent: true });
            if (data?.tournament_matches) {
              const updated = data.tournament_matches.find((m) => m.id === matchId);
              if (updated) setSelectedMatch(updated);
            }
          }}
        />

        {/* Remove Team Confirmation Modal */}
        <ConfirmModal
          isOpen={!!teamToRemoveId}
          onClose={() => setTeamToRemoveId(null)}
          onConfirm={handleConfirmRemoveTeam}
          title="Remove team"
          message="Remove this team from the tournament?"
          confirmText="Remove"
          cancelText="Cancel"
          variant="danger"
        />

        <ConfirmModal
          isOpen={!!teamToWithdrawId}
          onClose={() => !withdrawingTeam && setTeamToWithdrawId(null)}
          onConfirm={handleConfirmWithdrawTeam}
          title="Mark team as withdrawn"
          message={`This team will be shown as withdrawn, sorted to the bottom of group standings, and excluded from automatic knockout qualifiers. Past results stay in the table. If they still have open group matches, the next step offers to mark those as walkovers (opponents win, ${walkoverPointsFor}–0 for standings); you can skip and handle scores manually.`}
          confirmText={withdrawingTeam ? 'Saving...' : 'Mark withdrawn'}
          cancelText="Cancel"
          variant="warning"
          confirmDisabled={withdrawingTeam}
        />

        <ConfirmModal
          isOpen={!!walkoverApplyPrompt}
          onClose={() => !applyingWalkovers && setWalkoverApplyPrompt(null)}
          onConfirm={handleConfirmApplyWalkoversAfterWithdraw}
          title="Mark remaining group matches as walkovers?"
          message={
            walkoverApplyPrompt
              ? `This withdrawn team has ${walkoverApplyPrompt.count} group match${walkoverApplyPrompt.count !== 1 ? 'es' : ''} still open (scheduled or in progress). Apply walkovers now so each opponent wins (counted as ${walkoverPointsFor}–0 for standings)? You can skip and handle matches manually.`
              : ''
          }
          confirmText={applyingWalkovers ? 'Applying...' : 'Apply walkovers'}
          cancelText="Skip"
          variant="warning"
          confirmDisabled={applyingWalkovers}
        />

        <ConfirmModal
          isOpen={!!teamToUnwithdrawId}
          onClose={() => !unwithdrawingTeam && setTeamToUnwithdrawId(null)}
          onConfirm={handleConfirmUnwithdrawTeam}
          title="Undo withdraw?"
          message="This team will return to normal standings order and be eligible for automatic knockout qualifiers again. Every group-stage walkover or forfeit that included this pair will be reset to scheduled (scores cleared) so you can play or enter results again. Knockout and normal played matches are unchanged."
          confirmText={unwithdrawingTeam ? 'Saving...' : 'Restore team'}
          cancelText="Cancel"
          variant="default"
          confirmDisabled={unwithdrawingTeam}
        />

        {/* Regenerate Group Matches Confirmation Modal */}
        <ConfirmModal
          isOpen={showRegenerateConfirm}
          onClose={() => setShowRegenerateConfirm(false)}
          onConfirm={handleRegenerateMatches}
          title="Regenerate Group Matches"
          message="This will permanently delete all existing group matches and scores, then regenerate matches from the current groups. Continue?"
          confirmText="OK"
          cancelText="Cancel"
          variant="warning"
        />

        <ConfirmModal
          isOpen={showReduceGroupCountConfirm}
          onClose={() => {
            setShowReduceGroupCountConfirm(false);
            setPendingReduceGroupCount(null);
          }}
          onConfirm={handleConfirmReduceGroupCount}
          title="Reduce number of groups?"
          message={
            pendingReduceGroupCount != null
              ? `You have ${groups.length} group${groups.length !== 1 ? 's' : ''}. Reducing to ${pendingReduceGroupCount} will remove the last group(s) and all team assignments and group matches for those groups. Teams in the remaining groups are unchanged. Continue?`
              : ''
          }
          confirmText={applyingGroupCount ? 'Applying…' : 'Reduce groups'}
          cancelText="Cancel"
          variant="warning"
          confirmDisabled={applyingGroupCount}
        />

        {/* Clear Groups Confirmation Modal */}
        <ConfirmModal
          isOpen={showClearGroupsConfirm}
          onClose={() => setShowClearGroupsConfirm(false)}
          onConfirm={handleClearGroups}
          title="Clear groups"
          message="This removes all groups, team assignments, and group-stage matches and sets the tournament back to draft so you can choose auto-split or manual assignment again. Continue?"
          confirmText={clearingGroups ? 'Clearing...' : 'Clear groups'}
          cancelText="Cancel"
          variant="danger"
          confirmDisabled={clearingGroups}
        />

        {/* Generate Knockout Bracket Modal */}
        <GenerateKnockoutModal
          isOpen={showGenerateKnockoutModal}
          onClose={() => setShowGenerateKnockoutModal(false)}
          groupStandings={groupStandings}
          progressionTopN={tournament?.progression_top_n ?? 2}
          progressionBestLosers={!!tournament?.progression_best_third}
          bestLosersRankingOrder={tournament?.best_losers_ranking_order}
          onGenerate={handleGenerateKnockout}
          generating={generatingKnockout}
        />

        {/* Edit Knockout Matchups Modal */}
        <EditKnockoutMatchupsModal
          isOpen={showEditMatchupsModal}
          onClose={() => setShowEditMatchupsModal(false)}
          matches={matches}
          onEditMatch={(match) => {
            setShowEditMatchupsModal(false);
            setMatchForEditMatchup(match);
          }}
          onRegenerateConfirm={() => {
            setShowEditMatchupsModal(false);
            setShowGenerateKnockoutModal(true);
          }}
          regenerating={false}
        />

        {/* Edit Knockout Matchup Modal (single match) */}
        <EditKnockoutMatchupModal
          isOpen={!!matchForEditMatchup}
          onClose={() => setMatchForEditMatchup(null)}
          match={matchForEditMatchup}
          teams={teams}
          onSave={handleSaveMatchup}
          saving={savingMatchup}
        />

      </div>
    </div>
  );
};

export default TournamentDetailPage;
